import { create } from "zustand";

const STARTER_FILES = {
  "package.json": JSON.stringify(
    {
      name: "omnicraft-preview",
      private: true,
      version: "0.0.0",
      type: "module",
      scripts: { dev: "vite", build: "vite build", preview: "vite preview" },
      dependencies: { react: "^18.3.1", "react-dom": "^18.3.1" },
      devDependencies: {
        "@types/react": "^18.3.3",
        "@types/react-dom": "^18.3.0",
        "@vitejs/plugin-react": "^4.3.1",
        typescript: "^5.5.3",
        vite: "^5.4.0",
      },
    },
    null,
    2,
  ),
  "index.html": `<!doctype html>
<html>
  <head>
    <meta charset="UTF-8" />
    <title>OmniCraft Preview</title>
    <script src="https://cdn.tailwindcss.com"></script>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.tsx"></script>
  </body>
</html>`,
  "vite.config.ts": `import { defineConfig } from "vite";
import react from "@vitejs/plugin-react";
export default defineConfig({ plugins: [react()] });`,
  "src/main.tsx": `import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
createRoot(document.getElementById("root")!).render(<App />);`,
  "src/App.tsx": `import React from "react";
export default function App() {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-950 text-white font-sans">
      <div className="text-center space-y-3">
        <p className="text-xs tracking-[0.3em] text-emerald-400">OMNICRAFT · READY</p>
        <h1 className="text-4xl font-semibold">Say what you want to build.</h1>
        <p className="text-white/50 max-w-md text-sm">
          Type a prompt on the right. OmniCraft plans it, streams the files, and hot-reloads
          this preview.
        </p>
      </div>
    </div>
  );
}`,
};

export const AGENTS = [
  { id: "orchestrator", label: "Orchestrator", tool: "route()" },
  { id: "architect", label: "Architect", tool: "plan()" },
  { id: "builder", label: "Builder", tool: "write_file()" },
  { id: "debugger", label: "Debugger / SRE", tool: "patch_file()" },
];

export const useOmniCraftStore = create((set, get) => ({
  files: STARTER_FILES,
  baseSnapshot: STARTER_FILES,
  activePath: "src/App.tsx",
  openTabs: ["src/App.tsx"],

  models: [],
  modelId: "claude-sonnet-4-6",

  askBeforeBuilding: true,
  plan: null,
  pendingPrompt: null,

  history: [],
  streamingText: "",
  streaming: false,
  streamPhase: null,

  swarm: {
    active: "orchestrator",
    paused: false,
    subtasks: [],
  },

  terminal: [],
  viewport: "desktop",
  previewNonce: 0,
  previewError: null,

  conflicts: [],

  session: { id: null, title: "Untitled build" },

  setModels: (models) => set({ models }),
  setModelId: (modelId) => set({ modelId }),
  setAskBeforeBuilding: (askBeforeBuilding) => set({ askBeforeBuilding }),
  setViewport: (viewport) => set({ viewport }),

  openFile: (path) =>
    set((s) => ({
      activePath: path,
      openTabs: s.openTabs.includes(path) ? s.openTabs : [...s.openTabs, path],
    })),
  closeTab: (path) =>
    set((s) => {
      const tabs = s.openTabs.filter((p) => p !== path);
      return {
        openTabs: tabs,
        activePath: s.activePath === path ? tabs[tabs.length - 1] || null : s.activePath,
      };
    }),

  setFileContent: (path, content) =>
    set((s) => ({ files: { ...s.files, [path]: content } })),

  applyFileMap: (map) =>
    set((s) => ({ files: { ...s.files, ...map } })),

  removeFile: (path) =>
    set((s) => {
      const files = { ...s.files };
      delete files[path];
      return {
        files,
        openTabs: s.openTabs.filter((p) => p !== path),
        activePath: s.activePath === path ? Object.keys(files)[0] || null : s.activePath,
      };
    }),

  snapshotBase: () => set((s) => ({ baseSnapshot: { ...s.files } })),

  appendHistory: (msg) => set((s) => ({ history: [...s.history, msg] })),

  setPlan: (plan) => set({ plan }),
  setPendingPrompt: (pendingPrompt) => set({ pendingPrompt }),

  setStreaming: (streaming, phase = null) =>
    set({ streaming, streamPhase: phase, streamingText: streaming ? "" : get().streamingText }),
  appendStreamingText: (t) =>
    set((s) => ({ streamingText: s.streamingText + t })),
  resetStreamingText: () => set({ streamingText: "" }),

  setSwarmActive: (active) =>
    set((s) => ({ swarm: { ...s.swarm, active } })),
  setSwarmPaused: (paused) =>
    set((s) => ({ swarm: { ...s.swarm, paused } })),
  addSubtask: (task) =>
    set((s) => ({
      swarm: {
        ...s.swarm,
        subtasks: [...s.swarm.subtasks, { id: crypto.randomUUID(), status: "in_progress", ...task }],
      },
    })),
  updateSubtask: (id, patch) =>
    set((s) => ({
      swarm: {
        ...s.swarm,
        subtasks: s.swarm.subtasks.map((t) => (t.id === id ? { ...t, ...patch } : t)),
      },
    })),
  clearSubtasks: () =>
    set((s) => ({ swarm: { ...s.swarm, subtasks: [] } })),

  pushLog: (line) =>
    set((s) => ({ terminal: [...s.terminal.slice(-500), { t: Date.now(), line }] })),
  clearLogs: () => set({ terminal: [] }),

  triggerPreview: () => set((s) => ({ previewNonce: s.previewNonce + 1 })),
  setPreviewError: (previewError) => set({ previewError }),

  addConflict: (conflict) =>
    set((s) => ({ conflicts: [...s.conflicts, conflict] })),
  resolveConflict: (path, resolution) =>
    set((s) => {
      const files = { ...s.files, [path]: resolution };
      return {
        files,
        conflicts: s.conflicts.filter((c) => c.path !== path),
      };
    }),

  setSession: (session) => set({ session }),
}));
